import { z } from "zod";
import { OpportunityStatusSchema } from "./opportunities";

export const AnalyticsOverviewTotalsSchema = z.object({
  draftCount: z.number(),
  finalizedCount: z.number(),
  archivedCount: z.number(),
  totalCount: z.number(),
});
export type AnalyticsOverviewTotals = z.infer<
  typeof AnalyticsOverviewTotalsSchema
>;

export const AnalyticsMonthlyStatSchema = z.object({
  month: z.string(),
  draftCount: z.number(),
  finalizedCount: z.number(),
  archivedCount: z.number(),
  totalCount: z.number(),
  revenue: z.string(),
});
export type AnalyticsMonthlyStat = z.infer<
  typeof AnalyticsMonthlyStatSchema
>;

export const AnalyticsOverviewResponseSchema = z.object({
  totals: AnalyticsOverviewTotalsSchema,
  monthly: z.array(AnalyticsMonthlyStatSchema),
});
export type AnalyticsOverviewResponse = z.infer<
  typeof AnalyticsOverviewResponseSchema
>;

export const TopLineItemSchema = z.object({
  itemId: z.string().nullable(),
  itemName: z.string().nullable(),
  description: z.string().nullable(),
  quoteCount: z.number(),
  totalRevenue: z.string(),
});
export type TopLineItem = z.infer<typeof TopLineItemSchema>;

export const TopLineItemsResponseSchema = z.object({
  items: z.array(TopLineItemSchema),
});
export type TopLineItemsResponse = z.infer<
  typeof TopLineItemsResponseSchema
>;

export const AnalyticsQuoteViewSchema = z.object({
  id: z.string(),
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().nullable(),
  durationSeconds: z.number().nullable(),
  ipAddress: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
});
export type AnalyticsQuoteView = z.infer<typeof AnalyticsQuoteViewSchema>;

export const QuoteViewsResponseSchema = z.object({
  views: z.array(AnalyticsQuoteViewSchema),
});
export type QuoteViewsResponse = z.infer<typeof QuoteViewsResponseSchema>;

export const ViewsPerWeekSchema = z.object({
  weekStart: z.string(),
  count: z.number(),
});
export type ViewsPerWeek = z.infer<typeof ViewsPerWeekSchema>;

export const TopCitySchema = z.object({
  city: z.string(),
  count: z.number(),
});
export type TopCity = z.infer<typeof TopCitySchema>;

export const ViewsSummaryResponseSchema = z.object({
  viewsPerWeek: z.array(ViewsPerWeekSchema),
  topCities: z.array(TopCitySchema),
});
export type ViewsSummaryResponse = z.infer<
  typeof ViewsSummaryResponseSchema
>;

export const OpportunityCountResponseSchema = z.object({
  count: z.coerce.number(),
});
export type OpportunityCountResponse = z.infer<
  typeof OpportunityCountResponseSchema
>;

export const OpportunityMetricsResponseSchema = z.object({
  totalDealSize: z.string(),
  wonDealSize: z.string(),
  avgDealSize: z.string().nullable(),
  winRate: z.number().nullable(),
  avgTimeToQuoteBusinessSeconds: z.number().nullable(),
  avgQuoteToCloseBusinessSeconds: z.number().nullable(),
  weightedTotalDealSize: z.string(),
  weightedWonDealSize: z.string(),
  weightedAvgDealSize: z.string().nullable(),
});
export type OpportunityMetricsResponse = z.infer<
  typeof OpportunityMetricsResponseSchema
>;

export const QuotesCountResponseSchema = z.object({
  count: z.coerce.number(),
});
export type QuotesCountResponse = z.infer<typeof QuotesCountResponseSchema>;

export const OpportunityValueOverTimePointSchema = z.object({
  weekStart: z.string(),
  value: z.string(),
  weightedValue: z.string(),
});
export type OpportunityValueOverTimePoint = z.infer<
  typeof OpportunityValueOverTimePointSchema
>;

export const OpportunityValueOverTimeResponseSchema = z.object({
  points: z.array(OpportunityValueOverTimePointSchema),
});
export type OpportunityValueOverTimeResponse = z.infer<
  typeof OpportunityValueOverTimeResponseSchema
>;

/** Row for per-opportunity timing used by analytics table + chart */
export const OpportunityTimingRowSchema = z.object({
  opportunityId: z.string(),
  opportunityName: z.string(),
  amount: z.string(),
  status: OpportunityStatusSchema,
  timeToQuoteBusinessSeconds: z.number().nullable(),
  timeToCloseBusinessSeconds: z.number().nullable(),
  agentWorkingSeconds: z.number(),
  waitingApprovalSeconds: z.number(),
});
export type OpportunityTimingRow = z.infer<typeof OpportunityTimingRowSchema>;

/** Aggregate agent timing metrics across opportunities */
export const AgentEfficiencyMetricsSchema = z.object({
  avgAgentWorkingSeconds: z.number().nullable(),
  avgWaitingApprovalSeconds: z.number().nullable(),
  agentWaitingPercent: z.number().nullable(),
});
export type AgentEfficiencyMetrics = z.infer<
  typeof AgentEfficiencyMetricsSchema
>;

/** Combined response for per-opportunity timing analytics */
export const OpportunityTimingAnalyticsResponseSchema = z.object({
  rows: z.array(OpportunityTimingRowSchema),
  aggregates: AgentEfficiencyMetricsSchema,
});
export type OpportunityTimingAnalyticsResponse = z.infer<
  typeof OpportunityTimingAnalyticsResponseSchema
>;

export const CustomerSummaryTotalsSchema = z.object({
  feedbackSent: z.number(),
  rfqsReceived: z.number(),
  quotesProcessed: z.number(),
  editsPerQuote: z.number().nullable(),
  oneTouchQuotes: z.number(),
  mediumTouchQuotes: z.number(),
  heavyTouchQuotes: z.number(),
});
export type CustomerSummaryTotals = z.infer<
  typeof CustomerSummaryTotalsSchema
>;

export const CustomerSummaryDailyPointSchema = z.object({
  date: z.string(),
  feedbackSent: z.number(),
  rfqsReceived: z.number(),
  quotesProcessed: z.number(),
  editsPerQuote: z.number().nullable(),
});
export type CustomerSummaryDailyPoint = z.infer<
  typeof CustomerSummaryDailyPointSchema
>;

export const CustomerSummaryRangeSchema = z.object({
  from: z.string(),
  to: z.string(),
});
export type CustomerSummaryRange = z.infer<typeof CustomerSummaryRangeSchema>;

export const CustomerSummaryPreviousSchema = z.object({
  range: CustomerSummaryRangeSchema,
  totals: CustomerSummaryTotalsSchema,
});
export type CustomerSummaryPrevious = z.infer<
  typeof CustomerSummaryPreviousSchema
>;

export const CustomerSummaryResponseSchema = z.object({
  range: CustomerSummaryRangeSchema,
  totals: CustomerSummaryTotalsSchema,
  previous: CustomerSummaryPreviousSchema,
  daily: z.array(CustomerSummaryDailyPointSchema),
});
export type CustomerSummaryResponse = z.infer<
  typeof CustomerSummaryResponseSchema
>;
